// AlertForm.jsx
/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { Form, Input, message, Divider, Skeleton } from "antd";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import {
  db,
  fbAuth,
  storage,
} from "../../../services/firebaseService/connection";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import SuperAdminContainer from "../SuperAdminContainer";
import { ChantButton, ScreenHeader } from "../../../CommonComponents";
import ChantInput from "../../../CommonComponents/ChantInput/ChantInput";
import ChantSelect from "../../../CommonComponents/ChantSelect/ChantSelect";
import ChantDatePicker from "../../../CommonComponents/ChantDatePicker/ChantDatePicker";
import ChantSwitch from "../../../CommonComponents/ChantSwitch/ChantSwitch";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import FUpload from "../../commons/formFields/FUpload";

const AlertForm = () => {
  const { alertId } = useParams();
  const isEditMode = !!alertId;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingAlert, setFetchingAlert] = useState(isEditMode);
  const [showCTA, setShowCTA] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const navigate = useNavigate();

  // Upload alert image to Firebase Storage
  const uploadAlertImage = async (file, id) => {
    try {
      const storageRef = ref(storage, `alerts/${id}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  // Get image layout from file
  const getImageLayout = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const layout =
            img.width > img.height
              ? "landscape"
              : img.width < img.height
                ? "portrait"
                : "square";
          resolve(layout);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Fetch existing alert if editing
  useEffect(() => {
    if (!isEditMode) return;

    const fetchAlert = async () => {
      try {
        const alertDoc = await getDoc(doc(db, "adminAlerts", alertId));
        if (!alertDoc.exists()) {
          toast.error("Alert not found");
          navigate("/superadmin/alerts");
          return;
        }

        const data = alertDoc.data();

        form.setFieldsValue({
          title: data.title,
          description: data.description,
          priority: data.priority,
          targetType: data.targetType || "global",
          expiresAt: data.expiresAt ? dayjs(data.expiresAt) : null,
          ctaText: data.ctaText || undefined,
          ctaUrl: data.ctaUrl || undefined,
        });

        // Same pattern as GroupDesign - existing URL goes into fileList
        if (data.imageUrl) {
          setFileList([{ uid: "-1", url: data.imageUrl }]);
        }

        if (data.ctaUrl) {
          setShowCTA(true);
        }
      } catch (error) {
        console.error("Error fetching alert:", error);
        toast.error("Failed to load alert");
        navigate("/superadmin/alerts");
      } finally {
        setFetchingAlert(false);
      }
    };

    fetchAlert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEditMode) {
        const updateData = {
          title: values.title,
          description: values.description,
          priority: values.priority || 2,
          targetType: values.targetType,
          targetGroupIds: [],
          expiresAt: values.expiresAt
            ? values.expiresAt.valueOf()
            : dayjs().add(90, "day").valueOf(),
        };

        // Only touch imageUrl if it actually changed
        if (imageChanged) {
          if (fileList.length === 0) {
            // Image was removed
            updateData.imageUrl = null;
            updateData.imageLayout = null;
          } else if (imageFile) {
            // New image uploaded - upload to Firebase Storage
            const imageUrl = await uploadAlertImage(imageFile, alertId);
            const imageLayout = await getImageLayout(imageFile);
            updateData.imageUrl = imageUrl;
            updateData.imageLayout = imageLayout;
          }
        }

        if (showCTA && values.ctaText && values.ctaUrl) {
          updateData.ctaUrl = values.ctaUrl;
          updateData.ctaText = values.ctaText;
        } else {
          updateData.ctaUrl = null;
          updateData.ctaText = null;
        }

        await updateDoc(doc(db, "adminAlerts", alertId), updateData);
        toast.success("Alert updated successfully!");
      } else {
        const alertData = {
          title: values.title,
          description: values.description,
          createdAt: Date.now(),
          createdBy: fbAuth?.currentUser?.uid,
          isActive: true,
          priority: values.priority || 2,
          targetType: values.targetType,
          targetGroupIds: [],
          expiresAt: values.expiresAt
            ? values.expiresAt.valueOf()
            : dayjs().add(90, "day").valueOf(),
          alertType: "informational",
        };

        // Create the document first to get the ID
        const docRef = await addDoc(collection(db, "adminAlerts"), alertData);

        // Prepare update data if needed
        const updates = {};

        // If there's an image, upload it
        if (imageFile) {
          const imageUrl = await uploadAlertImage(imageFile, docRef.id);
          const imageLayout = await getImageLayout(imageFile);
          updates.imageUrl = imageUrl;
          updates.imageLayout = imageLayout;
        }

        // Add CTA fields if enabled
        if (showCTA && values.ctaText && values.ctaUrl) {
          updates.ctaUrl = values.ctaUrl;
          updates.ctaText = values.ctaText;
        }

        // Update document with image and/or CTA in one call if needed
        if (Object.keys(updates).length > 0) {
          await updateDoc(docRef, updates);
        }

        toast.success("Alert created successfully!");
      }

      navigate("/superadmin/alerts");
    } catch (error) {
      console.error("Error saving alert:", error);
      message.error("Failed to save alert. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    setImageChanged(true);

    // Store the actual file object for upload
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      setImageFile(newFileList[0].originFileObj);
    } else {
      setImageFile(null);
    }
  };

  const handleImageRemove = () => {
    setFileList([]);
    setImageFile(null);
    setImageChanged(true);
  };

  if (fetchingAlert) {
    return (
      <SuperAdminContainer>
        <ScreenHeader title="Edit Alert" />
        <div
          style={{
            marginTop: "2px",
            backgroundColor: "var(--primary-white-background)",
            padding: "12px",
          }}
        >
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </SuperAdminContainer>
    );
  }

  return (
    <SuperAdminContainer>
      <ScreenHeader title={isEditMode ? "Edit Alert" : "Create Alert"} />
      <div
        style={{
          marginTop: "2px",
          backgroundColor: "var(--primary-white-background)",
          padding: "12px",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ priority: 2, targetType: "global" }}
        >
          <Form.Item
            name="title"
            rules={[
              { required: true, message: "Please enter alert title" },
              { max: 100, message: "Title must be less than 100 characters" },
            ]}
            style={{ marginBottom: 32 }}
          >
            <ChantInput
              label="Title"
              placeholder="e.g., New Dark Mode Available"
              size="large"
              required
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please enter alert description" },
            ]}
            style={{ marginBottom: 32 }}
          >
            <Input.TextArea
              rows={4}
              placeholder="Write your announcement message here..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <FUpload
            label="Image"
            fileList={fileList}
            onChange={handleImageChange}
            onRemove={handleImageRemove}
            accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
            style={{ marginBottom: 32 }}
          />

          <Form.Item name="priority" style={{ marginBottom: 32 }}>
            <ChantSelect
              label="Priority"
              helpText="Higher priority alerts show first"
              size="large"
              options={[
                { label: "🔴 High", value: 1 },
                { label: "🟡 Medium", value: 2 },
                { label: "🟢 Low", value: 3 },
              ]}
            />
          </Form.Item>

          <Form.Item name="targetType" style={{ marginBottom: 32 }}>
            <ChantSelect
              label="Target"
              helpText="Who should see this alert"
              size="large"
              options={[{ label: "All Groups", value: "global" }]}
            />
          </Form.Item>

          <Form.Item name="expiresAt" style={{ marginBottom: 32 }}>
            <ChantDatePicker
              label="Expires At"
              helpText="Alert will stop showing after this date. Defaults to 90 days."
              style={{ width: "100%" }}
              size="large"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              placeholder="Select expiry date (default: 90 days)"
            />
          </Form.Item>

          <div style={{ marginBottom: showCTA ? 32 : 16 }}>
            <ChantSwitch
              label="Add Action Button"
              checked={showCTA}
              onChange={setShowCTA}
            />
            <div style={{ marginTop: 8, color: "#666", fontSize: "14px" }}>
              Adds a "Go" button that navigates users
            </div>
          </div>

          {showCTA && (
            <>
              <Form.Item
                name="ctaText"
                rules={[
                  { required: showCTA, message: "Please enter button text" },
                ]}
                style={{ marginBottom: 32 }}
              >
                <ChantInput
                  label="Button Text"
                  placeholder="e.g., Go to Settings"
                  size="large"
                  required
                />
              </Form.Item>

              <Form.Item
                name="ctaUrl"
                rules={[
                  { required: showCTA, message: "Please enter redirect route" },
                  { pattern: /^\//, message: "Route must start with /" },
                ]}
                style={{ marginBottom: 32 }}
              >
                <ChantInput
                  label="Redirect Route"
                  helpText="Internal route path"
                  placeholder="/settings/appearance"
                  size="large"
                  required
                />
              </Form.Item>
            </>
          )}

          <Divider />

          <Form.Item style={{ marginTop: 24 }}>
            <div className="flex-center">
              <ChantButton
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
              >
                {isEditMode ? "Update Alert" : "Create Alert"}
              </ChantButton>
            </div>
          </Form.Item>
        </Form>
      </div>
    </SuperAdminContainer>
  );
};

export default AlertForm;
